import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { LiaWhatsapp } from "react-icons/lia";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

import "../../../assets/scss/custom/pages/_status-page.scss";
import sessionConnectedIcon from "../../../assets/channel-session/sessionconnected.svg";
import sessionDisconnectedIcon from "../../../assets/channel-session/sessionDisconnected.svg";
import titleNameJesss from "../../../assets/channel-session/titlenamejesss.svg";
import { URLS } from "../../../constants";
import { toast, ToastContainer } from "react-toastify";
import { Session } from "../types";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { normalizePhoneNumberForParsing } from "../../../utils/formatPhoneNumber";
import { AccountNameForm } from "./NewProfile";
import { WebhookQrModal } from "./GetQRCode";
import { NewSessionModal } from "./NewSessionModal";
import { PhoneModal } from "./PhoneModal";
import {
  connectWebSocket,
  handleWebSocketMessage,
  handleWebSocketError,
  StateType,
} from "../../../utils/webSocketUtils";
import api from "../../../utils/api";
import SettingsIcon from "@mui/icons-material/Settings";
import WebhookConfigDialog from "./WebhookConfigDialog";

import Container from "@mui/material/Container";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import Tooltip from "@mui/material/Tooltip";
import { Chip, Paper } from "@mui/material";
import { SessionSyncBadge } from "../../../components/SessionSyncBadge";
import logger from "../../../utils/logger";
import { useDashboardTheme } from "../context/DashboardThemeContext";

// Definição de cores e estilos para uso consistente
const styleVars = {
  primaryColor: "#4C3699",
  primaryLight: "rgba(76, 54, 153, 0.1)",
  secondaryColor: "#FF6B6B",
  textPrimary: "#333333",
  textSecondary: "#6E6E6E",
  statusConnected: "#4CAF50",
  statusDisconnected: "#414042",
  statusScan: "#FFA500",
  statusStarting: "#4C3699",
  backgroundLight: "#FAFAFA",
  cardShadow: "0 4px 8px rgba(0, 0, 0, 0.05)",
  cardShadowHover: "0 8px 16px rgba(0, 0, 0, 0.1)",
  borderRadiusSm: "8px",
  borderRadiusMd: "12px",
  borderRadiusLg: "16px",
  transition: "all 0.2s ease-in-out",
  spacing: {
    xs: "8px",
    sm: "16px",
    md: "24px",
    lg: "32px",
    xl: "48px",
  },
};

interface StatusPageProps {
  role: string;
  sessions: Session[];
  updateSessions?: () => void;
  loadingSearch: boolean;
  loadingControl: (value: boolean) => void;
  activeTour: () => void;
  isAgentEnabled?: boolean;
  onRefresh?: () => void;
}

const StatusPage = forwardRef<HTMLButtonElement, StatusPageProps>(
  (
    {
      sessions,
      role,
      updateSessions,
      loadingSearch,
      loadingControl,
      activeTour,
      isAgentEnabled,
      onRefresh,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const isDark = useDashboardTheme();

    const textPrimary = isDark ? "#ffffff" : styleVars.textPrimary;
    const textSecondary = isDark ? "#a0a0a0" : styleVars.textSecondary;
    const cardBg = isDark ? "rgba(30, 30, 30, 0.95)" : "#ffffff";
    const cardBorder = isDark ? "1px solid rgba(255,255,255,0.08)" : "none";
    const inputBg = isDark
      ? "rgba(255,255,255,0.05)"
      : "rgba(255,255,255,0.67)";

    // Debug: Verificar se o botão QR Code foi renderizado
    useEffect(() => {
      const qrButton = document.querySelector("#dashboard-qr-code-button");
      if (qrButton) {
        // Listener para o evento customizado do tour
        const handleTourEvent = (_: any) => {};

        window.addEventListener("tour-qr-step-ready", handleTourEvent);

        return () => {
          window.removeEventListener("tour-qr-step-ready", handleTourEvent);
        };
      }
    }, []);

    // Controls dropdown for session deletion
    const [dropdownAnchor, setDropdownAnchor] = useState<null | HTMLElement>(
      null,
    );
    const [dropdownSessionName, setDropdownSessionName] = useState<
      string | null
    >(null);

    // Component states
    const [message, setMessage] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [filteredSessions, setFilteredSessions] = useState<Session[]>([]);
    const [accountModalOpen, setAccountModalOpen] = useState<boolean>(false);
    const [qrCodeModalOpen, setQrCodeModalOpen] = useState<boolean>(false);
    const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
    const [showWebhookConfig, setShowWebhookConfig] = useState(false);
    const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
    const [sessionToDelete, setSessionToDelete] = useState<Session | null>(
      null,
    );
    const [socket, setSocket] = useState<WebSocket | null>(null);
    const [state, setState] = useState<StateType>({
      qrCode: null,
      showQrCode: false,
      webSocketLoading: true,
      loading: false,
      status: null,
      error: null,
    });

    // Track sync progress for each session
    // Rastreia progresso de sincronização para cada sessão ~temporario~
    const [sessionSyncProgress, setSessionSyncProgress] = useState<
      Record<string, { progress: number; total: number; status: string }>
    >({});

    // Controls the channel selection modal
    const [newSessionModalOpen, setNewSessionModalOpen] =
      useState<boolean>(false);
    const [selectedApiType, setSelectedApiType] = useState<string>("");

    // Controls the warning when closing the QR Code modal without scanning
    const [showQrWarning, setShowQrWarning] = useState(false);

    const buttonRef = useRef<HTMLButtonElement>(null);
    useImperativeHandle(ref, () => buttonRef.current as HTMLButtonElement);

    useEffect(() => {
      setFilteredSessions(sessions);
    }, [sessions]);

    // Listen for sync progress events via useGlobalWebSocket CustomEvents
    // WebSocket manual - usar CustomEvent do useGlobalWebSocket
    useEffect(() => {
      const handleGlobalSyncProgress = (event: CustomEvent) => {
        const data = event.detail;
        if (!data || !data.sessionId) {
          return;
        }

        setSessionSyncProgress((prev) => ({
          ...prev,
          [data.sessionId]: {
            progress: data.processed || data.progress || 0,
            total: data.total || 400,
            status: data.status || "syncing",
          },
        }));

        // Clear progress when complete
        if (data.status === "complete") {
          setTimeout(() => {
            setSessionSyncProgress((prev) => {
              const newState = { ...prev };
              delete newState[data.sessionId];
              return newState;
            });
          }, 3000);
        }
      };

      window.addEventListener(
        "websocket-sync-progress",
        handleGlobalSyncProgress as any,
      );
      return () => {
        window.removeEventListener(
          "websocket-sync-progress",
          handleGlobalSyncProgress as any,
        );
      };
    }, []);

    // Formats phone number using libphonenumber-js to ensure correct mask per country
    const formatPhoneNumber = (phoneNumber: string): string => {
      if (!phoneNumber) return "";
      try {
        const normalized = normalizePhoneNumberForParsing(phoneNumber);
        if (!normalized) return phoneNumber;
        const parsed = parsePhoneNumberFromString(normalized);
        if (parsed) return parsed.formatInternational();
      } catch {}
      return phoneNumber;
    };

    // Returns a label based on session status
    const getStatusLabel = (status: string): string => {
      switch (status) {
        case "WORKING":
          return t("Connected");
        case "STOPPED":
          return t("Disconnected");
        case "SCAN_QR_CODE":
          return t("Scan QR Code");
        case "STARTING":
          return t("Starting");
        default:
          return t("Disconnected");
      }
    };

    // Returns a color for the status pill
    const getStatusColor = (status: string): string => {
      switch (status) {
        case "WORKING":
          return "#2E7D32"; // Verde mais escuro
        case "STOPPED":
          return "#C62828"; // Vermelho mais escuro
        case "SCAN_QR_CODE":
          return "#FFA500";
        case "STARTING":
          return "#4C3699";
        default:
          return "#9E9E9E";
      }
    };

    // Returns background color for status chip
    const getStatusBgColor = (status: string): string => {
      switch (status) {
        case "WORKING":
          return "#E8F5E9"; // Verde claro
        case "STOPPED":
          return "#FFEBEE"; // Vermelho claro
        case "SCAN_QR_CODE":
          return "rgba(255, 165, 0, 0.1)";
        case "STARTING":
          return "rgba(76, 54, 153, 0.1)";
        default:
          return "rgba(158, 158, 158, 0.1)";
      }
    };

    // Returns status chip class name
    const getStatusClassName = (status: string): string => {
      switch (status) {
        case "WORKING":
          return "connected";
        case "STOPPED":
          return "disconnected";
        case "SCAN_QR_CODE":
          return "scan";
        case "STARTING":
          return "starting";
        default:
          return "disconnected";
      }
    };

    // Opens the QR Code modal if status is SCAN_QR_CODE
    const handlePillClick = (status: string) => {
      if (status === "SCAN_QR_CODE") {
        toggleQrCodeModal();
      }
    };

    const handleDropdownClick = (
      event: React.MouseEvent<HTMLButtonElement>,
      sessionName: string,
    ) => {
      setDropdownAnchor(event.currentTarget);
      setDropdownSessionName(sessionName);
    };
    const handleDropdownClose = () => {
      setDropdownAnchor(null);
      setDropdownSessionName(null);
    };

    // Deletes a session via API
    const handleDeleteSession = async () => {
      if (sessionToDelete) {
        if (loadingControl) loadingControl(true);
        try {
          const response = await api.post("/api/session_delete", {
            name: sessionToDelete.name,
          });
          if (response.status < 200 || response.status >= 300) {
            throw new Error("Network response was not ok");
          }
          setMessage(t("Session deleted successfully!"));
          setSessionToDelete(null);
          setConfirmModalOpen(false);
          if (updateSessions) updateSessions();
        } catch (error) {
          logger.error("Failed to delete session:", error);
        } finally {
          if (loadingControl) loadingControl(false);
        }
      }
    };

    const confirmDeleteSession = (session: Session) => {
      setSessionToDelete(session);
      setConfirmModalOpen(true);
    };

    // Updates the profile picture for a session
    const handleUpdateProfilePicture = async (session: Session) => {
      if (!session.id) {
        logger.error("Session ID está undefined ou vazio");
        toast.error(t("Failed to update profile picture"));
        return;
      }

      if (!session.phone_number) {
        toast.error(t("Session does not have a phone number connected"));
        return;
      }

      if (loadingControl) loadingControl(true);

      try {
        const response = await api.post("/api/update-session-profile-picture", {
          session_id: session.id,
        });

        if (response.status === 200) {
          toast.success(t("Profile picture updated successfully!"));

          // Update local state with the new profile picture URL
          const updatedSessions = sessions.map((s) =>
            s.id === session.id
              ? { ...s, profile_pic_url: response.data.profilePicURL }
              : s,
          );
          setFilteredSessions(updatedSessions);

          // Trigger full refresh to ensure consistency
          if (updateSessions) {
            setTimeout(() => updateSessions(), 500);
          }
        }
      } catch (error: any) {
        let userMessage = t("Failed to update profile picture");

        if (error.response?.status === 404) {
          userMessage = t("Session not found");
        } else if (error.response?.status === 401) {
          userMessage = t("Authentication failed");
        }
        toast.error(userMessage);
      } finally {
        if (loadingControl) loadingControl(false);
      }
    };

    // Toggle Jesss for a specific session
    // Alternar Jesss para uma sessão específica ~temporario~
    const handleToggleSessionJesss = async (session: Session | undefined) => {
      if (!session) {
        toast.error(t("toggle_jesss_session_error") || "Sessão não encontrada");
        return;
      }

      if (!session.id) {
        toast.error(
          t("toggle_jesss_session_error") || "ID da sessão não encontrado",
        );
        return;
      }

      const currentToggle = session.toggle !== false; // Default to true if undefined
      const newToggle = !currentToggle;

      if (loadingControl) loadingControl(true);

      try {
        const response = await api.post("/api/session-toggle-ia", {
          session_id: session.id,
          toggle: newToggle,
        });

        if (response.status === 200) {
          toast.success(
            newToggle
              ? t("jesss_enabled_session") || "Jesss ativada para esta sessão"
              : t("jesss_disabled_session") ||
                  "Jesss desativada para esta sessão",
          );

          // Update local state
          // Atualiza estado local ~temporario~
          const updatedSessions = sessions.map((s) =>
            s.id === session.id ? { ...s, toggle: newToggle } : s,
          );
          setFilteredSessions(updatedSessions);

          // Trigger full refresh
          // Dispara refresh completo ~temporario~
          if (updateSessions) {
            setTimeout(() => updateSessions(), 500);
          }
        }
      } catch (error: any) {
        logger.error("Failed to toggle session Jesss:", error);
        toast.error(t("Failed to update session settings"));
      } finally {
        if (loadingControl) loadingControl(false);
      }
    };

    // Filters sessions according to the search term
    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = event.target.value;
      setSearchTerm(searchValue);
      if (!searchValue) {
        setFilteredSessions(sessions);
      } else {
        const filtered = sessions.filter((s) =>
          s.name.toLowerCase().includes(searchValue.toLowerCase()),
        );
        setFilteredSessions(filtered);
      }
    };

    // Controls the PhoneModal open state
    const [phoneModalOpen, setPhoneModalOpen] = useState<boolean>(false);

    // Stores the company phone number for the new session
    const [companyPhone, setCompanyPhone] = useState<string | undefined>(
      undefined,
    );

    // Handler for confirming phone modal (implement logic as needed)
    const onPhoneConfirm = (phone: string) => {
      setCompanyPhone(phone);
      setPhoneModalOpen(false);
      setAccountModalOpen(true);
    };

    const closeAllModals = () => {
      setPhoneModalOpen(false);
      setAccountModalOpen(false);
      setQrCodeModalOpen(false);
      setShowQrWarning(false);
      setNewSessionModalOpen(false);
    };

    // Toggles the account creation modal
    const toggleAccountModal = (channel: string) => {
      if (sessions.length >= 4) {
        toast.error(t("Session limit"));
      } else {
        setSelectedChannel(channel);
        // Pular o PhoneModal e ir direto para o AccountModal
        setAccountModalOpen(true);

        // Se o tour estiver rodando, finalizá-lo quando o modal "Criar sessão" abrir
        setTimeout(() => {
          const tourRunning = document.querySelector(
            '[data-tour-elem="spotlight"]',
          );
          const jessModal = document.querySelector('[class*="JessModal"]');
          if (tourRunning || jessModal) {
            // Disparar evento customizado para finalizar o tour
            window.dispatchEvent(
              new CustomEvent("tour-finish", {
                detail: {
                  reason: "qr-modal-opened",
                  modalType: "create-session",
                },
              }),
            );
          }
        }, 500); // Delay para garantir que o modal seja renderizado
      }
    };

    const openNewSessionModal = () => {
      if (sessions.length >= 4) {
        toast.error(t("Session limit"));
        return;
      }
      setSelectedApiType("");
      setNewSessionModalOpen(true);
    };

    const handleNewSessionContinue = () => {
      setNewSessionModalOpen(false);
      setSelectedChannel(selectedApiType);
      setAccountModalOpen(true);

      setTimeout(() => {
        const tourRunning = document.querySelector(
          '[data-tour-elem="spotlight"]',
        );
        const jessModal = document.querySelector('[class*="JessModal"]');
        if (tourRunning || jessModal) {
          window.dispatchEvent(
            new CustomEvent("tour-finish", {
              detail: {
                reason: "qr-modal-opened",
                modalType: "create-session",
              },
            }),
          );
        }
      }, 500);
    };

    const closeAccountModal = () => {
      setAccountModalOpen(false);
    };

    // Toggles the QR Code modal, connecting/disconnecting from the WebSocket
    const toggleQrCodeModal = () => {
      if (!qrCodeModalOpen) {
        const newSocket = connectWebSocket(
          URLS.WEBSOCKET_URL + "/api/ws",
          (event) => {
            handleWebSocketMessage(
              event,
              setState,
              t("QR code successfully scanned!"),
              () => {
                if (updateSessions) updateSessions();
                closeAllModals();
              },
            );
          },
          (event) =>
            handleWebSocketError(
              event,
              setState,
              t("WebSocket error observed"),
            ),
        );
        setSocket(newSocket);
      } else {
        if (socket) {
          socket.close();
          setSocket(null);
        }
      }
      setQrCodeModalOpen(!qrCodeModalOpen);
    };

    // Intercepts modal closure by backdrop or ESC when the QR Code is visible
    const handleCloseQrModal = (event: object, reason: string) => {
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        setShowQrWarning(true);
      } else {
        toggleQrCodeModal();
      }
    };

    const toggleConfirmModal = () => {
      setConfirmModalOpen(!confirmModalOpen);
    };

    // Truncates text with ellipsis
    const truncateText = (text: string, maxLength: number): string => {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    };

    return (
      <Container
        className="status-page-container"
        sx={{
          py: 4,
          maxWidth: "1200px !important",
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <ToastContainer />

        {/* Loading spinner */}
        <Box
          sx={{
            display: loadingSearch ? "flex" : "none",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <CircularProgress sx={{ color: styleVars.primaryColor }} />
        </Box>

        {/* Renders sessions if available */}
        {sessions.length > 0 && !loadingSearch && (
          <Box sx={{ mb: 3 }}>
            {/* Search bar and the new session button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 3,
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
                width: "100%",
              }}
            >
              {/* Search bar */}
              <Box
                sx={{
                  flex: 1,
                  position: "relative",
                  width: "100%",
                }}
              >
                <SearchIcon
                  sx={{
                    position: "absolute",
                    left: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: textSecondary,
                  }}
                />
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder={t("Search sessions...")}
                  value={searchTerm}
                  onChange={handleSearch}
                  InputProps={{
                    sx: {
                      backgroundColor: inputBg,
                      borderRadius: styleVars.borderRadiusMd,
                      boxShadow: styleVars.cardShadowHover,
                      height: "56px",
                      pl: 5,
                      transition: styleVars.transition,
                      "&:hover": {
                        boxShadow: styleVars.cardShadowHover,
                      },
                      "&.Mui-focused": {
                        boxShadow: styleVars.cardShadowHover,
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: isDark
                          ? "rgba(255,255,255,0.1)"
                          : "transparent",
                      },
                      "&:hover fieldset": {
                        borderColor: isDark
                          ? "rgba(255,255,255,0.2)"
                          : "rgba(0,0,0,0.23)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: styleVars.primaryColor,
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      color: isDark ? "#ffffff" : undefined,
                      "&::placeholder": {
                        color: textSecondary,
                        opacity: 0.8,
                      },
                    },
                  }}
                />
              </Box>
              {/* Botão de Refresh // TODO: remover depois */}
              {onRefresh && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Atualizar sessões" arrow placement="top">
                    <IconButton
                      onClick={onRefresh}
                      sx={{
                        color: textPrimary,
                        "&:hover": {
                          backgroundColor: styleVars.backgroundLight,
                        },
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {role === "admin" && (
                <Box
                  sx={{
                    position: { xs: "fixed", sm: "relative" },
                    bottom: { xs: 88, sm: "auto" },
                    right: { xs: 16, sm: "auto" },
                    zIndex: 1300,
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <Tooltip title={t("New Session")} arrow placement="top">
                    <Button
                      variant="contained"
                      disabled={sessions.length >= 4}
                      onClick={openNewSessionModal}
                      sx={{
                        minWidth: { xs: 48, sm: "160px" },
                        height: { xs: 48, sm: "56px" },
                        borderRadius: { xs: "50%", sm: "8px" },
                        px: { xs: 0, sm: 3 },
                        fontSize: { xs: 0, sm: "0.875rem" },
                        fontWeight: 600,
                        textTransform: "none",
                        boxShadow: styleVars.cardShadow,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: styleVars.cardShadowHover,
                          transform:
                            sessions.length < 4 ? "translateY(-3px)" : "none",
                        },
                        "& .MuiButton-startIcon": {
                          margin: 0,
                        },
                      }}
                      startIcon={
                        <WhatsAppIcon
                          sx={{
                            fontSize: { xs: "1.4rem", sm: "1.2rem" },
                            color: "#25D366",
                          }}
                        />
                      }
                    >
                      <Box sx={{ display: { xs: "none", sm: "block" } }}>
                        {t("New Session")}
                      </Box>
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </Box>

            {filteredSessions.length === 0 && searchTerm && (
              <Box
                sx={{
                  color: "red",
                  fontSize: 14,
                  mt: 0.5,
                  mb: 2,
                  textAlign: "center",
                }}
              >
                {t("No results found")}
              </Box>
            )}

            {/* Session cards */}
            <Grid container spacing={3}>
              {filteredSessions.map((session) => (
                <Grid size={{ xs: 12, sm: 6, md: 6 }} key={session.name}>
                  <Paper
                    elevation={0}
                    onClick={() => handlePillClick(session.status)}
                    sx={{
                      position: "relative",
                      p: 3,
                      mb: 2,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      minHeight: "50px",
                      width: "100%",
                      bgcolor: cardBg,
                      border: cardBorder,
                      borderRadius: styleVars.borderRadiusMd,
                      boxShadow: isDark
                        ? "0 4px 8px rgba(0,0,0,0.4)"
                        : styleVars.cardShadow,
                      transition: styleVars.transition,
                      cursor: "pointer",
                      overflow: "hidden",
                      "&:hover": {
                        boxShadow: styleVars.cardShadowHover,
                        transform: "translateY(-4px)",
                      },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "4px",
                        backgroundColor: getStatusColor(session.status),
                        borderTopLeftRadius: styleVars.borderRadiusMd,
                        borderBottomLeftRadius: styleVars.borderRadiusMd,
                      },
                    }}
                  >
                    {/* Options menu */}
                    {role === "admin" && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDropdownClick(e, session.name);
                        }}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          p: 0.5,
                          color: textSecondary,
                          opacity: 0.7,
                          transition: styleVars.transition,
                          "&:hover": {
                            color: styleVars.primaryColor,
                            backgroundColor: styleVars.primaryLight,
                            transform: "rotate(90deg)",
                          },
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}

                    {/* WhatsApp icon on the left + session information */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {/* ícone ou foto de perfil */}
                      <Box
                        className="session-icon"
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: session.profile_pic_url
                            ? "50%"
                            : styleVars.borderRadiusSm,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          overflow: "hidden",
                          border: session.profile_pic_url
                            ? "2px solid #4CAF50"
                            : "none",
                        }}
                      >
                        <img
                          src={
                            session.profile_pic_url
                              ? session.profile_pic_url
                              : session.status === "WORKING"
                                ? sessionConnectedIcon
                                : sessionDisconnectedIcon
                          }
                          alt={
                            session.profile_pic_url
                              ? session.name
                              : session.status === "WORKING"
                                ? "Connected"
                                : "Disconnected"
                          }
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: session.profile_pic_url
                              ? "cover"
                              : "contain",
                          }}
                          onError={(e) => {
                            // Fallback para ícone padrão se a imagem falhar
                            e.currentTarget.src =
                              session.status === "WORKING"
                                ? sessionConnectedIcon
                                : sessionDisconnectedIcon;
                            e.currentTarget.style.objectFit = "contain";
                          }}
                        />
                      </Box>

                      {/* name and telephone */}
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: "1rem",
                            color: textPrimary,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: "150px",
                          }}
                        >
                          {truncateText(session.name, 15)}
                        </Typography>
                        {session.phone_number && (
                          <Typography
                            sx={{
                              fontSize: "0.875rem",
                              color: textSecondary,
                              mt: 0.5,
                            }}
                          >
                            {formatPhoneNumber(session.phone_number)}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* status chip and sync badge */}
                    <Box
                      sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}
                    >
                      <Chip
                        label={getStatusLabel(session.status)}
                        onClick={() => handlePillClick(session.status)}
                        sx={{
                          backgroundColor: getStatusBgColor(session.status),
                          color: getStatusColor(session.status),
                          fontWeight: 600,
                          borderRadius: "20px",
                          height: "28px",
                          "& .MuiChip-label": { px: 1.5 },
                          transition: styleVars.transition,
                        }}
                      />

                      {/* Sync progress badge (only for non-first sessions) */}
                      {/* Badge de progresso de sincronização (apenas para sessões não-primeira) ~temporario~ */}
                      {sessionSyncProgress[session.id] && (
                        <SessionSyncBadge
                          progress={sessionSyncProgress[session.id].progress}
                          total={sessionSyncProgress[session.id].total}
                          isSyncing={
                            sessionSyncProgress[session.id].status === "syncing"
                          }
                          isCompleted={
                            sessionSyncProgress[session.id].status ===
                            "complete"
                          }
                        />
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Instruction screen */}
        {sessions.length === 0 && !loadingSearch && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              p: { xs: 1.5, sm: 2.5 },
              maxWidth: "1000px",
              mx: "auto",
            }}
          >
            {/* Centered title with SVG logo */}
            <Box
              sx={{
                mb: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {(() => {
                const fullText = t("Connect the Jess to your WhatsApp");
                const parts = fullText.split("Jesss");
                return (
                  <>
                    <Typography
                      variant="h4"
                      component="span"
                      sx={{
                        fontWeight: 700,
                        color: styleVars.primaryColor,
                        fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                      }}
                    >
                      {parts[0]}
                    </Typography>
                    <img
                      src={titleNameJesss}
                      alt="Jesss"
                      style={{
                        height: "40px",
                        objectFit: "contain",
                      }}
                    />
                    <Typography
                      variant="h4"
                      component="span"
                      sx={{
                        fontWeight: 700,
                        color: styleVars.primaryColor,
                        fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                      }}
                    >
                      {parts[1]}
                    </Typography>
                  </>
                );
              })()}
            </Box>

            {/* Flexible container for instructions and QR button */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "center", md: "flex-start" },
                justifyContent: "space-between",
                width: "100%",
                gap: 2.5,
                mb: 3,
              }}
            >
              {/* Numbered instructions */}
              <Box
                sx={{
                  flex: 1,
                  textAlign: "left",
                  pr: { md: 4 },
                  maxWidth: { md: "60%" },
                }}
              >
                <ol
                  style={{
                    paddingLeft: "1.2rem",
                    margin: 0,
                    color: textPrimary,
                    listStylePosition: "outside",
                  }}
                >
                  <li style={{ marginBottom: "0.8rem", lineHeight: 1.4 }}>
                    {t("Open WhatsApp on your phone")}
                  </li>
                  <li style={{ marginBottom: "0.8rem", lineHeight: 1.4 }}>
                    {t("Tap Menu on Android, or Settings on iPhone")}
                  </li>
                  <li style={{ marginBottom: "0.8rem", lineHeight: 1.4 }}>
                    {t("Tap Connected Devices and then Connect a device")}
                  </li>
                  <li style={{ marginBottom: "0.8rem", lineHeight: 1.4 }}>
                    {t(
                      "Press the button next to it, add a name to this session and point your phone's camera at the QR CODE that will appear.",
                    )}
                  </li>
                </ol>
              </Box>

              {/* QR Code Button */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  maxWidth: { md: "40%" },
                }}
              >
                <Button
                  id="dashboard-qr-code-button"
                  ref={buttonRef}
                  variant="contained"
                  onClick={openNewSessionModal}
                  sx={{
                    width: { xs: "200px", sm: "220px" },
                    height: { xs: "200px", sm: "220px" },
                    borderRadius: styleVars.borderRadiusMd,
                    transition: styleVars.transition,
                    background:
                      "linear-gradient(180deg, #93F090 0%, #60BFB7 100%)",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 3,
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: styleVars.cardShadowHover,
                      background:
                        "linear-gradient(180deg, #93F090 0%, #60BFB7 100%)",
                    },
                  }}
                >
                  <LiaWhatsapp
                    style={{
                      fontSize: "3.5rem",
                      marginBottom: "1rem",
                      color: "white",
                    }}
                  />
                  <Typography
                    sx={{
                      color: "white",
                      fontWeight: 500,
                      textAlign: "center",
                      textTransform: "none",
                      fontSize: "1rem",
                      lineHeight: 1.4,
                    }}
                  >
                    {t("New Session")}
                  </Typography>
                </Button>
              </Box>
            </Box>

            {/* Separator */}
            <Box
              sx={{
                width: "100%",
                height: "1px",
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.1)",
                my: 2.5,
              }}
            />

            {/* Supporting text */}
            <Typography
              sx={{
                color: textSecondary,
                textAlign: "center",
                maxWidth: "600px",
                mx: "auto",
                mb: 2,
              }}
            >
              {t(
                "We support the Official Meta WhatsApp API. If you already have access, contact us",
              )}
            </Typography>

            {/* Link de ajuda */}
            <Button
              type="button"
              onClick={activeTour}
              sx={{
                color: styleVars.primaryColor,
                fontWeight: 500,
                "&:hover": {
                  backgroundColor: styleVars.primaryLight,
                },
              }}
            >
              {t("Need help?")}
            </Button>
          </Box>
        )}

        {/* Dropdown menu for actions in sessions */}
        <Menu
          anchorEl={dropdownAnchor}
          open={Boolean(dropdownAnchor) && dropdownSessionName !== null}
          onClose={handleDropdownClose}
          PaperProps={{
            sx: {
              borderRadius: styleVars.borderRadiusMd,
              boxShadow: styleVars.cardShadowHover,
              overflow: "hidden",
              minWidth: 280,
              bgcolor: isDark ? "rgba(30, 30, 30, 0.98)" : undefined,
              border: isDark ? "1px solid rgba(255,255,255,0.08)" : undefined,
            },
          }}
        >
          {/* Profile Picture Display */}
          {(() => {
            const currentSession = filteredSessions.find(
              (sess) => sess.name === dropdownSessionName,
            );
            return currentSession ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  p: 3,
                  borderBottom: isDark
                    ? "1px solid rgba(255,255,255,0.08)"
                    : "1px solid #e0e0e0",
                  backgroundColor: isDark
                    ? "rgba(40, 40, 40, 0.95)"
                    : "#fafafa",
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: currentSession.profile_pic_url
                      ? "3px solid #4CAF50"
                      : "3px solid #e0e0e0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: isDark ? "rgba(50,50,50,0.9)" : "#fff",
                    mb: 1.5,
                  }}
                >
                  <img
                    src={
                      currentSession.profile_pic_url
                        ? currentSession.profile_pic_url
                        : currentSession.status === "WORKING"
                          ? sessionConnectedIcon
                          : sessionDisconnectedIcon
                    }
                    alt={currentSession.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: currentSession.profile_pic_url
                        ? "cover"
                        : "contain",
                    }}
                    onError={(e) => {
                      e.currentTarget.src =
                        currentSession.status === "WORKING"
                          ? sessionConnectedIcon
                          : sessionDisconnectedIcon;
                      e.currentTarget.style.objectFit = "contain";
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "1rem",
                    color: textPrimary,
                    mb: 0.5,
                  }}
                >
                  {currentSession.name}
                </Typography>
                {currentSession.phone_number && (
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      color: textSecondary,
                    }}
                  >
                    {formatPhoneNumber(currentSession.phone_number)}
                  </Typography>
                )}
              </Box>
            ) : null;
          })()}

          {/* Update Profile Picture Option */}
          <MenuItem
            onClick={() => {
              const s = filteredSessions.find(
                (sess) => sess.name === dropdownSessionName,
              );
              if (s) {
                handleUpdateProfilePicture(s);
              }
              handleDropdownClose();
            }}
            sx={{
              p: 2,
              transition: styleVars.transition,
              "&:hover": {
                backgroundColor: styleVars.primaryLight,
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WhatsAppIcon sx={{ color: styleVars.primaryColor }} />
              <Typography sx={{ color: textPrimary }}>
                {t("Update Profile Picture")}
              </Typography>
            </Box>
          </MenuItem>

          {/* Toggle Jesss for this session */}
          {/* Toggle Jesss para esta sessão ~temporario~ */}
          <MenuItem
            onClick={() => {
              const s = filteredSessions.find(
                (sess) => sess.name === dropdownSessionName,
              );
              if (s) {
                handleToggleSessionJesss(s);
              } else {
                logger.error(
                  "[MenuItem Toggle] Session not found for name:",
                  dropdownSessionName,
                );
                toast.error("Sessão não encontrada na lista");
              }
              handleDropdownClose();
            }}
            sx={{
              p: 2,
              transition: styleVars.transition,
              "&:hover": {
                backgroundColor: styleVars.primaryLight,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isAgentEnabled
                      ? "linear-gradient(135deg, #34A853 0%, #71c8aa 100%)"
                      : "rgba(158, 158, 158, 0.2)",
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: "white",
                    }}
                  />
                </Box>
                <Typography sx={{ color: textPrimary }}>
                  {(() => {
                    const currentSession = filteredSessions.find(
                      (sess) => sess.name === dropdownSessionName,
                    );
                    const isEnabled = currentSession?.toggle !== false;
                    return isEnabled ? t("Disable Jesss") : t("Enable Jesss");
                  })()}
                </Typography>
              </Box>
            </Box>
          </MenuItem>

          {/* Webhook Config - only for API_OFFICIAL sessions */}
          {(() => {
            const currentSession = filteredSessions.find(
              (sess) => sess.name === dropdownSessionName,
            );
            return currentSession?.channel === "API_OFFICIAL" ? (
              <MenuItem
                onClick={() => {
                  setShowWebhookConfig(true);
                  handleDropdownClose();
                }}
                sx={{
                  p: 2,
                  transition: styleVars.transition,
                  "&:hover": {
                    backgroundColor: styleVars.primaryLight,
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <SettingsIcon sx={{ color: styleVars.primaryColor }} />
                  <Typography sx={{ color: textPrimary }}>
                    {t("webhook_config.title")}
                  </Typography>
                </Box>
              </MenuItem>
            ) : null;
          })()}

          <MenuItem
            onClick={() => {
              const s = filteredSessions.find(
                (sess) => sess.name === dropdownSessionName,
              );
              if (s) {
                confirmDeleteSession(s);
              }
              handleDropdownClose();
            }}
            sx={{
              p: 2,
              transition: styleVars.transition,
              color: "#F44336",
              "&:hover": {
                backgroundColor: "rgba(244, 67, 54, 0.1)",
              },
            }}
          >
            {t("Delete session")}
          </MenuItem>
        </Menu>

        {/* Modais */}
        <PhoneModal
          open={phoneModalOpen}
          onClose={() => setPhoneModalOpen(false)}
          onConfirm={onPhoneConfirm}
        />

        {role === "admin" && (
          <>
            {/* New session type selection modal */}
            <NewSessionModal
              open={newSessionModalOpen}
              onClose={() => setNewSessionModalOpen(false)}
              selectedApiType={selectedApiType}
              onSelectedApiTypeChange={setSelectedApiType}
              onContinue={handleNewSessionContinue}
            />

            {/* Account creation modal */}
            <Dialog
              open={accountModalOpen}
              onClose={toggleAccountModal}
              PaperProps={{
                sx: {
                  borderRadius: styleVars.borderRadiusLg,
                  bgcolor: isDark ? "rgba(20, 20, 20, 0.98)" : undefined,
                  border: isDark
                    ? "1px solid rgba(255,255,255,0.08)"
                    : undefined,
                },
              }}
            >
              <DialogTitle
                sx={{
                  fontSize: "25px",
                  color: styleVars.primaryColor,
                }}
              >
                {t("Create Session")}
              </DialogTitle>
              <DialogContent>
                <AccountNameForm
                  isOpen={accountModalOpen}
                  toggle={() => setAccountModalOpen(false)}
                  updateSessions={updateSessions}
                  channel={selectedChannel ?? undefined}
                  companyPhone={companyPhone}
                  onAllModalsClose={closeAllModals}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={closeAccountModal}
                  sx={{
                    color: textSecondary,
                    "&:hover": {
                      backgroundColor: styleVars.backgroundLight,
                    },
                  }}
                >
                  {t("Close")}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Modal de QR Code */}
            <Dialog
              open={qrCodeModalOpen}
              onClose={handleCloseQrModal}
              maxWidth="sm"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: styleVars.borderRadiusLg,
                  bgcolor: isDark ? "rgba(20, 20, 20, 0.98)" : undefined,
                  border: isDark
                    ? "1px solid rgba(255,255,255,0.08)"
                    : undefined,
                  color: isDark ? "#ffffff" : undefined,
                },
              }}
            >
              <DialogTitle
                sx={{
                  color: styleVars.primaryColor,
                  fontWeight: 600,
                }}
              >
                {t("QR Code")}
              </DialogTitle>
              <DialogContent>
                <WebhookQrModal
                  isOpen={qrCodeModalOpen}
                  toggle={toggleQrCodeModal}
                  existingSocket={socket}
                  qrCode={state.qrCode}
                  showQrCode={state.showQrCode}
                  updateSessions={updateSessions}
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={toggleQrCodeModal}
                  sx={{
                    color: textSecondary,
                    "&:hover": {
                      backgroundColor: styleVars.backgroundLight,
                    },
                  }}
                >
                  {t("Close")}
                </Button>
              </DialogActions>
            </Dialog>

            {/* QR Code Not Scanned Warning */}
            {showQrWarning && (
              <Dialog
                open={showQrWarning}
                onClose={() => setShowQrWarning(false)}
                PaperProps={{
                  sx: {
                    borderRadius: styleVars.borderRadiusLg,
                    bgcolor: isDark ? "rgba(20, 20, 20, 0.98)" : undefined,
                    border: isDark
                      ? "1px solid rgba(255,255,255,0.08)"
                      : undefined,
                  },
                }}
              >
                <DialogTitle sx={{ color: textPrimary }}>
                  {t("Atenção")}
                </DialogTitle>
                <DialogContent>
                  <Typography sx={{ color: textSecondary }}>
                    {t(
                      "Please scan the QR Code to start a WhatsApp session. If you close it, you'll need to click the card again to recover it. Do you want to close it?",
                    )}
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={() => setShowQrWarning(false)}
                    sx={{
                      color: textSecondary,
                      "&:hover": {
                        backgroundColor: styleVars.backgroundLight,
                      },
                    }}
                  >
                    {t("Cancelar")}
                  </Button>
                  <Button
                    onClick={() => {
                      setShowQrWarning(false);
                      toggleQrCodeModal();
                    }}
                    color="error"
                    sx={{
                      backgroundColor: "rgba(244, 67, 54, 0.1)",
                      "&:hover": {
                        backgroundColor: "rgba(244, 67, 54, 0.2)",
                      },
                    }}
                  >
                    {t("Fechar")}
                  </Button>
                </DialogActions>
              </Dialog>
            )}

            {/* Modal de confirmação de exclusão */}
            <Dialog
              open={confirmModalOpen}
              onClose={toggleConfirmModal}
              PaperProps={{
                sx: {
                  borderRadius: styleVars.borderRadiusLg,
                  bgcolor: isDark ? "rgba(20, 20, 20, 0.98)" : undefined,
                  border: isDark
                    ? "1px solid rgba(255,255,255,0.08)"
                    : undefined,
                },
              }}
            >
              <DialogTitle sx={{ color: textPrimary }}>
                {t("Confirm Delete")}
              </DialogTitle>
              <DialogContent>
                <Typography sx={{ color: textSecondary }}>
                  {t("Are you sure you want to delete this session?")}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={toggleConfirmModal}
                  sx={{
                    color: textSecondary,
                    "&:hover": {
                      backgroundColor: styleVars.backgroundLight,
                    },
                  }}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  onClick={handleDeleteSession}
                  color="error"
                  sx={{
                    backgroundColor: "rgba(244, 67, 54, 0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(244, 67, 54, 0.2)",
                    },
                  }}
                >
                  {t("Delete")}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}

        <WebhookConfigDialog
          open={showWebhookConfig}
          onClose={() => setShowWebhookConfig(false)}
        />
      </Container>
    );
  },
);

export default StatusPage;
